import {
  DEFAULT_UNIT,
  canonicalUnit,
  isExactVolume,
  toMilliliters
} from './units';

/// The result of parsing a free-text ingredient amount like "1.5 oz" or
/// "2 dashes".
///
/// Deliberately a named object rather than the positional tuple this function
/// used to return: the old `[number, string, 'ml']` shape hardcoded 'ml' as
/// the unit at every call site, which is how every ingredient written through
/// the app ended up labelled "ml" regardless of what the user typed.
export interface ParsedMeasure {
  /// The amount, expressed in `quantityType` units -- NOT in millilitres.
  /// This is the value stored in `recipe_ingredients.quantity`.
  quantity: number;
  /// Normalized display amount ("0.75", "1 1/2"), without the unit.
  quantityStr: string;
  /// Canonical unit. An unrecognized token is preserved verbatim (lowercased)
  /// rather than coerced to a guess.
  quantityType: string;
  /// Millilitre equivalent, or `undefined` when no honest conversion exists
  /// (count/ratio/weight units and unrecognized tokens).
  ml?: number;
  /// True only when `ml` came from an exact volume factor. False for
  /// approximate pours like `dash`, and for anything with no `ml` at all.
  exact: boolean;
}

/// Matches the leading amount of a free-text measure: mixed numbers
/// ("1 1/2"), fractions ("3/4"), decimals (".75", "0.75") and integers.
/// Anything after it is treated as unit text. There may be no amount at all
/// ("Top"), in which case the whole string is unit text.
const AMOUNT_RE = /^(\d+\s+\d+\/\d+|\d+\/\d+|\d*\.\d+|\d+)/;

export function parseMeasure(input: string): ParsedMeasure {
  const raw = (input ?? '').trim().toLowerCase();
  if (!raw) {
    throw new Error(`Invalid measurement format: '${input}'`);
  }

  const amountMatch = raw.match(AMOUNT_RE);
  const amountText = amountMatch ? amountMatch[1] : '';
  const unitText = raw.slice(amountText.length).trim();

  // A bare unit with no number ("Top", "Pinch", "Full jar") means one of it.
  // The previous implementation threw here, and init-jams-cocktails.ts
  // swallowed the error -- silently dropping those ingredients entirely.
  const quantity = amountText ? parseQuantity(amountText) : 1;

  // No unit at all means the house default (see DEFAULT_UNIT). An unknown
  // unit is a different case entirely and must never land on the default.
  const quantityType = unitText
    ? (canonicalUnit(unitText) ?? normalizeUnknownUnit(unitText))
    : DEFAULT_UNIT;

  const ml = toMilliliters(quantity, quantityType);

  return {
    quantity,
    quantityStr: formatAmount(quantity),
    quantityType,
    ml,
    exact: ml !== undefined && isExactVolume(quantityType)
  };
}

/// Keeps an unrecognized unit token intact so no information is invented or
/// lost. The last word is used because trailing-unit garnish phrasing is
/// common in the source data ("1 orange twist"); a multi-word leftover with
/// no recognizable unit is kept whole.
///
/// TODO(https://linear.app/lqd/issue/LQD-98/llm-based-ingredient-quantity-parsing)
/// This is the seam for an LLM fallback: anything reaching here is a unit
/// string the deterministic vocabulary in units.ts doesn't know yet.
function normalizeUnknownUnit(unitText: string): string {
  const words = unitText.split(/\s+/).filter(Boolean);
  for (let i = words.length - 1; i >= 0; i--) {
    const known = canonicalUnit(words[i]);
    if (known) {
      return known;
    }
  }
  return unitText;
}

/// Renders a parsed amount back to a compact display string, avoiding the
/// float noise of e.g. 1/3 -> "0.3333333333333333".
///
/// Six decimal places, trailing zeros stripped. That is enough to be lossless
/// for every amount in the real data -- a dash is 1/32 oz = 0.03125, which a
/// coarser rounding would corrupt -- while still collapsing repeating
/// fractions.
export function formatAmount(amount: number): string {
  if (!Number.isFinite(amount)) {
    return '';
  }
  if (Number.isInteger(amount)) {
    return String(amount);
  }
  return String(parseFloat(amount.toFixed(6)));
}

/// Parses the numeric half of a measure: mixed numbers ("1 1/2"), fractions
/// ("3/4"), decimals (".75") and integers.
export function parseQuantity(quantityStr: string): number {
  const trimmed = quantityStr.trim();

  // Mixed numbers like '1 1/2'
  if (/\s/.test(trimmed)) {
    const parts = trimmed.split(/\s+/);
    const wholeNumber = parseFloat(parts[0]);
    const fraction = parseFraction(parts[1]);
    if (isNaN(wholeNumber)) {
      throw new Error(`Invalid quantity: '${quantityStr}'`);
    }
    return wholeNumber + fraction;
  }

  // Fractions like '1/2'
  if (trimmed.includes('/')) {
    return parseFraction(trimmed);
  }

  const number = parseFloat(trimmed);
  if (isNaN(number)) {
    throw new Error(`Invalid quantity: '${quantityStr}'`);
  }
  return number;
}

function parseFraction(fractionStr: string): number {
  const parts = fractionStr.split('/');
  if (parts.length !== 2) {
    throw new Error(`Invalid fraction: '${fractionStr}'`);
  }
  const numerator = parseFloat(parts[0]);
  const denominator = parseFloat(parts[1]);
  if (isNaN(numerator) || isNaN(denominator) || denominator === 0) {
    throw new Error(`Invalid fraction: '${fractionStr}'`);
  }
  return numerator / denominator;
}
